import { useEffect, useState } from "react";
import type { CompatibilityInfo, Game } from "../types/game";
import { crossplayLabel, getCompatibility } from "../lib/api";

function fmtBool(v?: boolean): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "Unknown";
}

export default function GameDetail({
  game,
  onBack,
}: {
  game: Game;
  onBack: () => void;
}) {
  const [compat, setCompat] = useState<CompatibilityInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setCompat(null);
    getCompatibility(game.title)
      .then((c) => alive && setCompat(c))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [game.title]);

  const rows: Array<[string, React.ReactNode]> = [
    ["Genres", game.genres.length ? game.genres.join(", ") : "—"],
    ["Platforms", game.platforms.length ? game.platforms.join(", ") : "—"],
    ["Release year", game.releaseYear ?? "—"],
    ["Rating", game.rating ? `★ ${game.rating.toFixed(1)} / 5` : "—"],
    [
      "Max players (online)",
      loading ? "…" : (compat?.maxOnlinePlayers ?? "—"),
    ],
    [
      "Max players (local)",
      loading ? "…" : (compat?.maxLocalPlayers ?? "—"),
    ],
    ["Online co-op", loading ? "…" : fmtBool(compat?.onlineCoop)],
    ["Local co-op", loading ? "…" : fmtBool(compat?.localCoop)],
    ["Split-screen", loading ? "…" : fmtBool(compat?.splitScreen)],
    [
      "Crossplay",
      loading ? "…" : crossplayLabel(compat?.crossplayStatus ?? "unknown"),
    ],
    [
      "Available on",
      game.stores && game.stores.length ? game.stores.join(", ") : "—",
    ],
  ];

  return (
    <div className="detail">
      <button className="back" onClick={onBack} type="button">
        ← Back to results
      </button>

      <div className="detail-head">
        {game.imageUrl && (
          <img className="detail-cover" src={game.imageUrl} alt={game.title} />
        )}
        <div className="detail-head-body">
          <h2>{game.title}</h2>
          <dl className="info-list">
            {rows.map(([k, v]) => (
              <div key={k} className="info-row">
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          {compat && compat.sources.length > 0 && (
            <p className="source-note">
              Multiplayer data from: {compat.sources.join(", ")}.
            </p>
          )}
          {compat && compat.sources.length === 0 && !loading && (
            <p className="source-note">
              No multiplayer data found in IGDB or Steam for this title.
            </p>
          )}
        </div>
      </div>

      {compat?.description && (
        <section>
          <h3>About</h3>
          <p className="desc">{compat.description}</p>
        </section>
      )}

      {compat?.trailerUrl && (
        <section>
          <h3>Trailer</h3>
          <video className="trailer" src={compat.trailerUrl} controls />
        </section>
      )}

      {game.screenshots && game.screenshots.length > 0 && (
        <section>
          <h3>Screenshots</h3>
          <div className="shots">
            {game.screenshots.slice(0, 6).map((s) => (
              <img key={s} src={s} alt="" loading="lazy" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
